import { useState, useEffect, useCallback, useRef } from 'react';
import { useAnalytics } from '@/lib/analytics';
import { useI18n } from '@/lib/i18n';
import { Alert } from '@/components/ui/Alert';
import { safeJsonParse } from './wallet/safe-json';
import { AccountCard } from './wallet/AccountCard';
import type { HoursBasis, WalletStatus } from './wallet/BalancePanel';
import { CostCalculatorCard } from './wallet/CostCalculatorCard';
import { BillingRecordsCard, type BillingRecordsTab } from './wallet/BillingRecordsCard';
import { defaultScenarioUsdPerHour, hoursFromBalance, personalUsdPerHour } from './wallet/balance-estimate';
import { initialEngine, initialRegion, readStoredEngine, readStoredRegion } from './wallet/calculator-engine';
import { effectiveQwenRegion } from './wallet/cost-model';
import { usePricing } from './wallet/usePricing';
import { useLatestUsageEngine } from './wallet/useLatestUsageEngine';
import './Wallet.scss';

export function Billing() {
  const { trackEvent } = useAnalytics();
  const { t } = useI18n();

  const [walletStatus, setWalletStatus] = useState<WalletStatus | null>(null);
  // Each card owns its own failure. The page-wide `loading` flag and shared
  // `error` string this replaces let any one of three fetches degrade all of
  // them; this one covers the balance fetch and nothing else.
  const [balanceError, setBalanceError] = useState<string | null>(null);
  // Starts true: the card must not paint "$0.00" before the first fetch lands.
  const [balanceLoading, setBalanceLoading] = useState(true);
  const [pageError, setPageError] = useState<string | null>(null);
  const fetchAttemptedRef = useRef(false);

  // ONE fetch for the two cards that price things. They are two cards but one
  // dataset, and letting each fetch its own would let a deploy leave them
  // quoting two different rate tables side by side — see `usePricing`.
  const pricing = usePricing();
  // The engine and Qwen region the calculator opens on when the visitor has
  // not picked them.
  const latest = useLatestUsageEngine();
  // ONE opening region for the page: the calculator opens on it and the balance
  // estimate below prices the same one, so a visitor never reads two hours
  // figures from two regions.
  const region = initialRegion(readStoredRegion(), latest?.region);
  const [recordsTab, setRecordsTab] = useState<BillingRecordsTab>('records');
  const recordsRef = useRef<HTMLDivElement>(null);
  const showAllRecords = () => {
    setRecordsTab('records');
    recordsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  // The Usage page's 30-day header figures, for the visitor's own average.
  const [usageSummary, setUsageSummary] = useState<{ spendMicroUsd: number; sessionSeconds: number } | null>(null);
  const [summarySettled, setSummarySettled] = useState(false);
  const averageUsdPerHour = personalUsdPerHour(usageSummary);
  const hoursBasis: HoursBasis =
    averageUsdPerHour !== null ? { kind: 'average', usdPerHour: averageUsdPerHour } : { kind: 'scenario' };
  // Without an average, the preset priced on the engine the calculator opens
  // on. Held back until both requests have answered, so the figure and its
  // label appear once instead of changing under the reader.
  const hoursLeft = walletStatus && summarySettled && latest !== undefined
    ? hoursFromBalance(
        walletStatus.balanceMicroUsd,
        averageUsdPerHour ??
          defaultScenarioUsdPerHour(
            pricing.pricing,
            initialEngine(readStoredEngine(), latest?.engine),
            effectiveQwenRegion(pricing.pricing, region)
          )
      )
    : null;

  // Captured during the first render, BEFORE the effect below strips `success`
  // out of the URL. Effects run in declaration order, so anything reading
  // window.location later would find the parameter already gone.
  const [cameFromCheckout] = useState(
    () => new URLSearchParams(window.location.search).get('success') === 'true'
  );

  const fetchWalletStatus = useCallback(async (fresh: boolean) => {
    try {
      // `fresh=1` reads past the KV cache. KV is eventually consistent, so for
      // up to a minute after a top-up the cached balance is still the
      // pre-payment one — while the ledger, which reads D1, already shows the
      // payment. That disagreement, on one page load, is what a user reported.
      const response = await fetch(`/api/wallet/status${fresh ? '?fresh=1' : ''}`, {
        credentials: 'include',
      });
      if (!response.ok) {
        if (response.status === 401) return;
        throw new Error('Failed to fetch wallet status');
      }
      const data = await safeJsonParse<WalletStatus>(response);
      if (data) setWalletStatus(data);
    } catch (err) {
      console.error('Error fetching wallet status:', err);
      setBalanceError(t('dashboard.wallet.errorFetchingBalance'));
    } finally {
      setBalanceLoading(false);
    }
  }, [t]);

  const fetchUsageSummary = useCallback(async () => {
    try {
      // Same request as the Usage page's header: the viewer's UTC offset, so
      // its window is the 30 dates they would name.
      const tzOffsetMinutes = -new Date().getTimezoneOffset();
      const response = await fetch(`/api/wallet/usage-summary?tzOffsetMinutes=${tzOffsetMinutes}`, {
        credentials: 'include',
      });
      if (!response.ok) return;
      const data = await safeJsonParse<{ spendMicroUsd: number; sessionSeconds: number }>(response);
      if (data) setUsageSummary(data);
    } catch (err) {
      // Without it the hours fall back to the calculator's opening preset.
      console.error('Error fetching usage summary:', err);
    } finally {
      setSummarySettled(true);
    }
  }, []);

  useEffect(() => {
    if (cameFromCheckout) {
      trackEvent('payment_completed', { source: 'redirect' });
      // Strip the parameter so a reload does not re-announce the payment.
      window.history.replaceState({}, '', '/dashboard/billing');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (fetchAttemptedRef.current) return;
    fetchAttemptedRef.current = true;
    fetchWalletStatus(cameFromCheckout);
    fetchUsageSummary();
    trackEvent('wallet_page_viewed', { page: 'billing' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="wallet-page">
      {cameFromCheckout && (
        <Alert variant="success" className="wallet-page__alert">
          {t('dashboard.wallet.paymentSuccess')}
        </Alert>
      )}

      {pageError && (
        <Alert variant="error" className="wallet-page__alert">{pageError}</Alert>
      )}

      <div className="wallet-page__header">
        <h1>{t('dashboard.billing.title')}</h1>
        <p>{t('dashboard.billing.subtitle')}</p>
      </div>

      <div className="wallet-page__grid">
        {/* One card, two panels. The balance panel reads the rates straight
            off `status`, so nothing extra has to be threaded through. */}
        <AccountCard
          status={walletStatus}
          loading={balanceLoading}
          error={balanceError}
          onError={setPageError}
          hoursLeft={hoursLeft}
          hoursBasis={hoursBasis}
          onShowAllRecords={showAllRecords}
        />
        {/* Records and credits right under the account card, as one tabbed
            card: they were two cards listing every top-up twice, and with
            the calculator and the rate card above them the records sat about
            three screens down. */}
        <BillingRecordsCard status={walletStatus} tab={recordsTab} onTab={setRecordsTab} cardRef={recordsRef} />
        {/* "What will this cost me" is the question a person opens a billing
            page with, so the calculator stays open; the full rate card is the
            reference they reach for only if the answer surprises them, and it
            folds inside the calculator, following its engine. */}
        <CostCalculatorCard
          {...pricing}
          balanceMicroUsd={walletStatus?.balanceMicroUsd ?? null}
          latestEngine={latest?.engine}
          openingRegion={region}
        />
      </div>
    </div>
  );
}
